import { ref, computed, watch } from 'vue'
import { useCompanyStore } from '../stores/company'
import { useShipmentStore } from '../stores/useShipmentStore'
import { useAuthStore } from '../stores/auth'
import { useAuth } from './useAuth'
import { useAssignedShipmentView } from './useAssignedShipmentView'
import { ShipmentsTableRepository } from '@/infrastructure/repositories/ShipmentsTableRepository'
import { normalizeTableRows } from '@/utils/bigQueryRowNormalizer'
import { isInTransit, isDelayed, isAdvanced, isCompleted, isDeparted, isArrived } from '../utils/shipmentFilterPredicates'

interface ShipmentStats {
  total: number
  inTransit: number
  delayed: number
  completed: number
  advanced: number
  departed: number
  arrived: number
}

/**
 * Lo único que leen los predicados de las tarjetas (shipmentFilterPredicates).
 * Las tarjetas se cuentan sobre TODOS los embarques de la compañía, así que se pide
 * a GET_SHIPMENTS_TABLE solo esta proyección, sin página: para TCC son ~11.000 filas
 * de 6 campos, no la colección completa de Firestore que se bajaba antes.
 */
const STATS_FIELDS = ['id', 'status', 'eta', 'etd', 'atd', 'ata']

const repository = new ShipmentsTableRepository()

// Cache global para mantener las estadísticas entre cambios de ruta
const globalStatsCache = {
  stats: null as ShipmentStats | null,
  companyId: null as string | null
}

// Carga en curso: la página y el componente de tarjetas llaman a este composable
// a la vez, y una sola consulta alcanza para los dos.
let pendingLoad: { companyId: string; token: object; promise: Promise<void> } | null = null


const initialStats = {
  total: 0,
  inTransit: 0,
  delayed: 0,
  completed: 0,
  advanced: 0,
  departed: 0,
  arrived: 0
}

const stats = ref<ShipmentStats>({ ...initialStats })
const isLoading = ref(false)

export function useShipmentStats() {
  const companyStore = useCompanyStore()
  const shipmentStore = useShipmentStore()
  const authStore = useAuthStore()
  const { currentUser } = useAuth()
  const { hasAssignedView, loadAssignedView } = useAssignedShipmentView()

  const resetStats = () => {
    stats.value = { ...initialStats }
    globalStatsCache.stats = null
    globalStatsCache.companyId = null
  }

  const calculateStats = (shipmentList: any[], companyId = companyStore.selectedCompanyId) => {
    stats.value = {
      total: shipmentList.length,
      inTransit: shipmentList.filter(s => isInTransit(s)).length,
      delayed: shipmentList.filter(s => isDelayed(s)).length,
      completed: shipmentList.filter(s => isCompleted(s)).length,
      advanced: shipmentList.filter(s => isAdvanced(s)).length,
      departed: shipmentList.filter(s => isDeparted(s)).length,
      arrived: shipmentList.filter(s => isArrived(s)).length
    }

    // Actualizar caché global
    globalStatsCache.stats = stats.value
    globalStatsCache.companyId = companyId ?? null
  }

  /**
   * Cuenta las tarjetas de una compañía. Con `force` ignora el caché (refresh
   * manual, alta o borrado de embarques).
   *
   * Con vista asignada no se consulta nada: el universo restringido lo trae la
   * tabla desde BigQuery y las stats se calculan sobre él (watch de abajo).
   */
  const loadStats = async (companyId: string, force = false): Promise<void> => {
    if (!force && globalStatsCache.stats && globalStatsCache.companyId === companyId) {
      stats.value = globalStatsCache.stats
      return
    }
    if (!force && pendingLoad?.companyId === companyId) return pendingLoad.promise

    const token = {}
    const promise = (async () => {
      isLoading.value = true
      try {
        await loadAssignedView(companyId, currentUser.value?.uid || '', {
          isAdmin: authStore.isAdminOrSuperAdmin,
        })
        if (hasAssignedView.value) return

        const response = await repository.fetchTable({
          params: { company: companyId },
          fields: STATS_FIELDS,
        })
        // Si el usuario cambió de compañía mientras respondía, estos números ya no son suyos
        if (companyStore.selectedCompanyId !== companyId) return

        const { shipments } = normalizeTableRows(response.data, [], false)
        calculateStats(shipments, companyId)
      } catch (error) {
        console.error('❌ [useShipmentStats] Error contando embarques:', error)
      } finally {
        isLoading.value = false
        if (pendingLoad?.token === token) pendingLoad = null
      }
    })()

    pendingLoad = { companyId, token, promise }
    return promise
  }

  // Observar cambios en la autenticación
  watch(() => currentUser.value, (newUser) => {
    if (!newUser) {
      // Si no hay usuario (sesión cerrada), resetear stats
      resetStats()
    }
  })

  // Observar cambios en la compañía seleccionada
  watch(() => companyStore.selectedCompanyId, async (newCompanyId) => {
    if (newCompanyId) {
      if (globalStatsCache.companyId !== newCompanyId) resetStats()
      await loadStats(newCompanyId)
    }
  }, { immediate: true })

  // Vista asignada: las stats siguen al subconjunto restringido que muestra la tabla
  watch(() => (hasAssignedView.value ? shipmentStore.bigQueryShipments : null), (rows) => {
    if (rows) calculateStats(rows)
  })

  // Método para forzar actualización
  const refreshStats = async () => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) return
    await loadStats(companyId, true)
  }

  // Getters computados para cada estadística
  const inTransitCount = computed(() => stats.value.inTransit)
  const delayedCount = computed(() => stats.value.delayed)
  const totalCount = computed(() => stats.value.total)
  const completedCount = computed(() => stats.value.completed)

  return {
    stats,
    isLoading,
    calculateStats,
    refreshStats,
    resetStats,
    inTransitCount,
    delayedCount,
    totalCount,
    completedCount
  }
}
